import { Point } from './point'
import { length } from './util'
import { normalizeAngle } from './angle'

export class Vector {
  constructor(readonly x: number, readonly y: number) {}

  add(v: Vector): Vector {
    return new Vector(this.x + v.x, this.y + v.y)
  }

  subtract(v: Vector): Vector {
    return new Vector(this.x - v.x, this.y - v.y)
  }

  length(): number {
    return length(this.x, this.y)
  }

  scale(s: number): Vector {
    return new Vector(this.x * s, this.y * s)
  }

  angle(): number {
    return normalizeAngle(Math.atan2(this.y, this.x))
  }

  rotate(theta: number): Vector {
    const x1 = this.x * Math.cos(theta) - this.y * Math.sin(theta)
    const y1 = this.x * Math.sin(theta) + this.y * Math.cos(theta)
    return new Vector(x1, y1)
  }

  dot(v: Vector): number {
    return this.x * v.x + this.y * v.y
  }

  angleBetween(v: Vector): number {
    const d = this.dot(v)
    const cosTheta = d / (this.length() * v.length())
    return Math.acos(cosTheta)
  }

  normalize(d: number = 1): Vector {
    const length = this.length()
    if ( length !== 0.0 ) {
      return this.scale(d / length)
    } else { throw new RangeError() }
  }

  toString(): String {
    return `[${this.x}, ${this.y}]`
  }
}

export function asPoint(v: Vector): Point {
  return new Point(v.x, v.y)
}

export function asVector(p: Point): Vector {
  return new Vector(p.x, p.y)
}

export function add(p: Point, v: Vector): Point {
  return new Point(p.x + v.x, p.y + v.y)
}

export function subtract(p: Point, v: Vector): Point {
  return new Point(p.x - v.x, p.y - v.y)
}

export function vector(p0: Point, p1: Point): Vector {
  return new Vector(p1.x - p0.x, p1.y - p0.y)
}

export function polar(r: number, theta: number): Vector {
  return new Vector(r*Math.cos(theta), r*Math.sin(theta))
}
